import { zodResolver } from "@hookform/resolvers/zod"
import { memo, useMemo, useRef, useState } from "react"
import { FaPlus } from "react-icons/fa"

import { useCreateAttendance } from "@/hooks/api/useAttendances"
import { usePermissions } from "@/hooks/usePermissions"
import { attendanceUpdateSchema } from "@/types/attendance"
import type {
  AddAttendanceProps,
  AttendanceCreateInput,
  AttendanceUpdateInput,
} from "@/types/attendance"
import { Button } from "@chakra-ui/react"

import AttendanceFormDialog from "./AttendanceFormDialog"

const AddAttendance = memo(
  ({ onSuccess, onCancel }: AddAttendanceProps = {}) => {
    const [isOpen, setIsOpen] = useState(false)
    const { hasPermission } = usePermissions()
    const createAttendanceMutation = useCreateAttendance()
    const addButtonRef = useRef<HTMLButtonElement | null>(null)

    if (!hasPermission("attendances:create")) {
      return null
    }

    const defaultValues = useMemo<AttendanceUpdateInput>(
      () => ({
        enrollment_id: "",
        class_session_id: "",
        student_id: "",
        class_date: "",
        class_time: "",
        status: "present",
        check_in_time: "",
        check_out_time: "",
        is_makeup_class: false,
        makeup_for_date: "",
        instructor_notes: "",
        skills_practiced: [],
        behavior_notes: "",
        parent_notification_sent: false,
        marked_by_user_id: "",
        notes: "",
      }),
      [],
    )

    const handleClose = () => {
      setIsOpen(false)
      onCancel?.()
    }

    const handleSubmit = async (data: AttendanceUpdateInput) => {
      // Cast to CreateInput since all required fields will be present after form validation
      await createAttendanceMutation.mutateAsync(data as AttendanceCreateInput)
      onSuccess?.()
    }

    return (
      <>
        <Button
          ref={addButtonRef}
          onClick={() => setIsOpen(true)}
          colorPalette="blue"
          size="sm"
          aria-label="Add new attendance"
        >
          <FaPlus fontSize="14px" aria-hidden="true" />
          Add Attendance
        </Button>

        <AttendanceFormDialog
          isOpen={isOpen}
          title="Add New Attendance"
          submitLabel="Create Attendance"
          resolver={zodResolver(attendanceUpdateSchema)}
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          onClose={handleClose}
          isSubmitting={createAttendanceMutation.isPending}
          finalFocusRef={addButtonRef}
          showSkipLink
          loadingText="Creating..."
        />
      </>
    )
  },
)

AddAttendance.displayName = "AddAttendance"

export default AddAttendance
export type { AddAttendanceProps }
